import React, { createContext, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  loadContinuityCarry,
  mergeCarryFromPreserved,
  syncCarryFromRoute,
  coalesceContinuityCarry,
  type CanonicallsContinuityCarry,
} from "../../lib/canonicallsContinuityCarry";
import {
  LIVING_EXAMPLE,
  resolveEffectiveCarry,
  seedLivingContinuityExampleIfEmpty,
} from "../../lib/canonicallsLivingExample";
import {
  applyFreshCanonicalizeCarry,
  isCanonicallsFreshCanonicalize,
} from "../../lib/canonicallsFreshDemo";
import { normalizeCanonicallsPathname } from "../../lib/canonicallsHost";
import { preferAheadTraversalStep } from "../../lib/canonicallsTraversal";
import type { PreservedCrossPost } from "../../lib/verifiedCrossPost";

type CarryContextValue = {
  freshDemoActive: boolean;
  livingExampleSuppressed: boolean;
  carry: CanonicallsContinuityCarry;
  effectiveCarry: CanonicallsContinuityCarry;
  isLivingExample: boolean;
  livingExample: typeof LIVING_EXAMPLE;
  refreshCarry: () => void;
  applyPreserved: (record: PreservedCrossPost) => void;
};

const CanonicallsContinuityCarryContext = createContext<CarryContextValue | null>(null);

export function CanonicallsContinuityCarryProvider({ children }: { children: React.ReactNode }) {
  const { pathname, search, hash } = useLocation();
  const freshDemoActive = useMemo(
    () => isCanonicallsFreshCanonicalize(pathname, search),
    [pathname, search],
  );
  const skipLivingSeedRef = useRef(false);
  const freshDemoLockRef = useRef(freshDemoActive);

  const [carry, setCarry] = useState<CanonicallsContinuityCarry>(() =>
    freshDemoActive ? applyFreshCanonicalizeCarry() : loadContinuityCarry(),
  );

  const refreshCarry = useCallback(() => {
    setCarry(loadContinuityCarry());
  }, []);

  const applyPreserved = useCallback((record: PreservedCrossPost) => {
    const prev = coalesceContinuityCarry(loadContinuityCarry());
    setCarry(
      mergeCarryFromPreserved(record, {
        lastTraversalStep: preferAheadTraversalStep("representation", prev.lastTraversalStep),
      }),
    );
  }, []);

  useLayoutEffect(() => {
    const onCanonicalize = normalizeCanonicallsPathname(pathname) === "/canonicalize";
    if (!onCanonicalize) {
      freshDemoLockRef.current = false;
    }
    if (freshDemoActive) {
      freshDemoLockRef.current = true;
      setCarry(applyFreshCanonicalizeCarry());
      skipLivingSeedRef.current = true;
      return;
    }
    if (!skipLivingSeedRef.current) {
      seedLivingContinuityExampleIfEmpty();
    } else {
      skipLivingSeedRef.current = false;
    }
    setCarry(syncCarryFromRoute(pathname, search, hash));
  }, [pathname, search, hash, freshDemoActive]);

  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === "canonicalls-continuity-carry:v1") refreshCarry();
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [refreshCarry]);

  useEffect(() => {
    return () => {
      freshDemoLockRef.current = false;
      skipLivingSeedRef.current = false;
    };
  }, []);

  const safeCarry = useMemo(() => coalesceContinuityCarry(carry), [carry]);
  const livingExampleSuppressed =
    freshDemoActive ||
    (normalizeCanonicallsPathname(pathname) === "/canonicalize" && freshDemoLockRef.current);

  const { effectiveCarry, isLivingExample } = useMemo(() => {
    const resolved = resolveEffectiveCarry(safeCarry, { disableLivingExample: livingExampleSuppressed });
    return {
      effectiveCarry: coalesceContinuityCarry(resolved.carry),
      isLivingExample: resolved.isLivingExample,
    };
  }, [safeCarry, livingExampleSuppressed]);

  const value = useMemo(
    () => ({
      freshDemoActive,
      livingExampleSuppressed,
      carry,
      effectiveCarry,
      isLivingExample,
      livingExample: LIVING_EXAMPLE,
      refreshCarry,
      applyPreserved,
    }),
    [
      freshDemoActive,
      livingExampleSuppressed,
      carry,
      effectiveCarry,
      isLivingExample,
      refreshCarry,
      applyPreserved,
    ],
  );

  return (
    <CanonicallsContinuityCarryContext.Provider value={value}>{children}</CanonicallsContinuityCarryContext.Provider>
  );
}

export function useCanonicallsContinuityCarry(): CarryContextValue {
  const ctx = useContext(CanonicallsContinuityCarryContext);
  if (!ctx) {
    const freshDemoActive =
      typeof window !== "undefined" &&
      isCanonicallsFreshCanonicalize(window.location.pathname, window.location.search);
    if (!freshDemoActive) seedLivingContinuityExampleIfEmpty();
    const carry = freshDemoActive ? applyFreshCanonicalizeCarry() : loadContinuityCarry();
    const livingExampleSuppressed = freshDemoActive;
    const resolved = resolveEffectiveCarry(coalesceContinuityCarry(carry), {
      disableLivingExample: livingExampleSuppressed,
    });
    return {
      freshDemoActive,
      livingExampleSuppressed,
      carry: coalesceContinuityCarry(carry),
      effectiveCarry: coalesceContinuityCarry(resolved.carry),
      isLivingExample: resolved.isLivingExample,
      livingExample: LIVING_EXAMPLE,
      refreshCarry: () => {},
      applyPreserved: mergeCarryFromPreserved,
    };
  }
  return ctx;
}
